import clsx from 'clsx';
import { useField, useFormikContext } from 'formik';
import {
  ReportSubType,
  ReportType,
  useReportFindManyQuery,
} from 'generated/graphql';
import { useEffect, useMemo, useState } from 'react';
import { IStageFormValues } from 'views/Project/components/Stages/StageForm/StageForm.interfaces';
import {
  FormikCheckboxFormField,
  FormikSelectFormField,
  InlineNotification,
  NotificationType,
  RadioButtonGroup,
} from '@spotted-zebra-uk/ui-components';
import styles from './StageForm.module.scss';

enum ReportGenerationOption {
  GENERATE = 'generate',
  DO_NOT_GENERATE = 'do-not-generate',
}

const MANAGER_OPTIONS = [
  {
    value: ReportGenerationOption.GENERATE,
    label: 'Generate',
    id: 'manager-generate',
  },
  {
    value: ReportGenerationOption.DO_NOT_GENERATE,
    label: 'Do not generate',
    id: 'manager-do-not-generate',
  },
];

const CANDIDATE_OPTIONS = [
  {
    value: ReportGenerationOption.GENERATE,
    label: 'Generate',
    id: 'candidate-generate',
  },
  {
    value: ReportGenerationOption.DO_NOT_GENERATE,
    label: 'Do not generate',
    id: 'candidate-do-not-generate',
  },
];

const ReportsSection = ({
  companyHideReports,
}: {
  companyHideReports: boolean;
}) => {
  const { setFieldValue, initialValues } = useFormikContext<IStageFormValues>();
  const [showDisabledReportsWarning, setShowDisabledReportsWarning] =
    useState(false);
  const [managerReportGenerate, setManagerReportGenerate] = useState(
    initialValues.managerReportGenerate
      ? ReportGenerationOption.GENERATE
      : ReportGenerationOption.DO_NOT_GENERATE,
  );
  const [candidateReportGenerate, setCandidateReportGenerate] = useState(
    initialValues.candidateReportGenerate
      ? ReportGenerationOption.GENERATE
      : ReportGenerationOption.DO_NOT_GENERATE,
  );
  const [showManagerReportWarning, setShowManagerReportWarning] = useState('');
  const [showCandidateReportWarning, setShowCandidateReportWarning] =
    useState('');

  const { data: reportsData } = useReportFindManyQuery({
    variables: {
      type: ReportType.StageResults,
    },
  });
  const companyDefaultManagerReportId = initialValues
    .companyDefaultManagerReport?.value
    ? parseInt(initialValues.companyDefaultManagerReport.value)
    : undefined;
  const companyDefaultCandidateReportId = initialValues
    .companyDefaultCandidateReport?.value
    ? parseInt(initialValues.companyDefaultCandidateReport?.value)
    : undefined;

  const managerReportOptions = useMemo(() => {
    return (
      reportsData?.report
        ?.filter(
          (report) => report.subType === ReportSubType.StageResultsManager,
        )
        .map((report) => ({
          value: report.id.toString(),
          label: report.name,
          labelDescription:
            report.id === companyDefaultManagerReportId ? 'Default' : undefined,
        })) || []
    );
  }, [reportsData, companyDefaultManagerReportId]);

  const candidateReportOptions = useMemo(() => {
    return (
      reportsData?.report
        ?.filter(
          (report) => report.subType === ReportSubType.StageResultsCandidate,
        )
        .map((report) => ({
          value: report.id.toString(),
          label: report.name,
          labelDescription:
            report.id === companyDefaultCandidateReportId
              ? 'Default'
              : undefined,
        })) || []
    );
  }, [reportsData, companyDefaultCandidateReportId]);

  useEffect(() => {
    if (managerReportGenerate === ReportGenerationOption.DO_NOT_GENERATE) {
      setFieldValue('managerReport', null);
      setFieldValue('managerReportGenerate', false);
    }
    if (candidateReportGenerate === ReportGenerationOption.DO_NOT_GENERATE) {
      setFieldValue('candidateReportGenerate', false);
      setFieldValue('candidateReport', null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [managerReportGenerate, candidateReportGenerate]);

  const handleManagerReportGenerateChange = (e: ReportGenerationOption) => {
    if (managerReportGenerate === ReportGenerationOption.DO_NOT_GENERATE) {
      setFieldValue('managerReportGenerate', true);
      setFieldValue('managerReport', initialValues.companyDefaultManagerReport);
      setShowManagerReportWarning(
        'Reports will only be generated for candidates who complete their assessment after this stage setting is saved.',
      );
      // If the company setting for reports is set to Disabled, then we show the warning when changing from "Do not generate" to "Generate"
      if (companyHideReports) {
        setShowDisabledReportsWarning(true);
      }
    } else {
      setShowManagerReportWarning(
        'Accessing any existing reports will be hidden from the company app.',
      );
      if (
        companyHideReports &&
        candidateReportGenerate === ReportGenerationOption.DO_NOT_GENERATE
      ) {
        setShowDisabledReportsWarning(false);
      }
    }
    setManagerReportGenerate(e);
  };

  const handleCandidateReportGenerateChange = (e: ReportGenerationOption) => {
    if (candidateReportGenerate === ReportGenerationOption.DO_NOT_GENERATE) {
      setFieldValue('candidateReportGenerate', true);
      setFieldValue(
        'candidateReport',
        initialValues.companyDefaultCandidateReport,
      );
      setShowCandidateReportWarning(
        'Reports will only be generated for candidates who complete their assessment after this stage setting is saved.',
      );
      // If the company setting for reports is set to Disabled, then we show the warning when changing from "Do not generate" to "Generate"
      if (companyHideReports) {
        setShowDisabledReportsWarning(true);
      }
    } else {
      setShowCandidateReportWarning(
        'Accessing any existing reports will be hidden from the company app.',
      );
      //   Cannot set email candidate report to true if candidate reports are not generated
      setFieldValue('stageEmailCandidateReport', false);
      if (
        companyHideReports &&
        managerReportGenerate === ReportGenerationOption.DO_NOT_GENERATE
      ) {
        setShowDisabledReportsWarning(false);
      }
    }
    setCandidateReportGenerate(e);
  };

  return (
    <section className={styles.reportsSection}>
      {showDisabledReportsWarning && (
        <InlineNotification
          notificationType={NotificationType.WARNING}
          className={styles.disabledReportsWarning}
        >
          Report enablement settings and reports cannot be accessed by company
          users via the company app.
        </InlineNotification>
      )}
      <p className={styles.reportsSectionTitle}> Hiring team report</p>
      <RadioButtonGroup
        id="managerReportGenerate"
        name="managerReportGenerate"
        value={managerReportGenerate.toString()}
        onChange={(e, id) =>
          handleManagerReportGenerateChange(e as ReportGenerationOption)
        }
        options={MANAGER_OPTIONS}
        className={styles.projectTeamReportRadioGroup}
      />
      {managerReportGenerate === 'generate' && (
        <FormikSelectFormField
          id="managerReport"
          name="managerReport"
          label="Report name"
          placeholder="Report name"
          options={managerReportOptions}
          useFormikField={useField}
          hasClearIndicator={false}
        />
      )}
      {showManagerReportWarning && (
        <InlineNotification
          notificationType={NotificationType.NEUTRAL}
          className={styles.reportsSectionWarning}
        >
          <p>{showManagerReportWarning}</p>
        </InlineNotification>
      )}
      <p className={styles.reportsSectionTitle}>Candidate report</p>
      <RadioButtonGroup
        id="candidateReportGenerate"
        name="candidateReportGenerate"
        value={candidateReportGenerate}
        onChange={(e, id) =>
          handleCandidateReportGenerateChange(e as ReportGenerationOption)
        }
        options={CANDIDATE_OPTIONS}
        className={styles.projectTeamReportRadioGroup}
      />
      {candidateReportGenerate === 'generate' && (
        <>
          {' '}
          <FormikSelectFormField
            id="candidateReport"
            name="candidateReport"
            label="Report name"
            placeholder="Report name"
            options={candidateReportOptions}
            useFormikField={useField}
            hasClearIndicator={false}
          />
          <FormikCheckboxFormField
            id="stageEmailCandidateReport"
            label="Email candidate the report"
            data-role="stage-candidate-email-report-check"
            useFormikField={useField}
          />
        </>
      )}
      {showCandidateReportWarning && (
        <InlineNotification
          notificationType={NotificationType.NEUTRAL}
          className={clsx(
            styles.reportsSectionWarning,
            styles.candidateReportWarning,
          )}
        >
          <p>{showCandidateReportWarning}</p>
        </InlineNotification>
      )}
    </section>
  );
};

export default ReportsSection;
